import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Switch,
  ScrollView,
  Alert,
  Modal,
} from "react-native";
import { RouteProp, useRoute } from "@react-navigation/native";
import { observer } from "mobx-react";
import { postStore } from "../stores/PostStore";
import { blogCategoryStore } from "../stores/BlogCategoryStore";
import { IPost } from "@interfaces/ipost";
import { EnumRoutesAppWeb } from "../enum/EnumRoutesAppWeb";
import { goBack } from "../services/NavigationService";
import { Theme } from "../enum/Theme";
import PhotoUploader from "./PhotoUploader";
import RichTextEditor from "./RichTextEditor";
import SpaceH from "@comps/SpaceH";
import SpaceW from "@comps/SpaceW";
import { ageRangeList } from "@enum/AgeRangeList";
import { Language } from "@constants/language";
import { LanguageSelector } from "../comps/LanguageSelector";

type PostFormRouteProp = RouteProp<
  { [EnumRoutesAppWeb.createEditBlog]: { post?: IPost } },
  EnumRoutesAppWeb.createEditBlog
>;

const PostForm = observer(() => {
  const route = useRoute<PostFormRouteProp>();
  const post: IPost | undefined = route.params?.post;

  const [lang, setLang] = useState<Language>(post?.lang ?? Language.PT);
  const [title, setTitle] = useState(post?.title || "");
  const [summary, setSummary] = useState(post?.summary || "");
  const [fullText, setFullText] = useState(post?.fullText || "");
  const [categories, setCategories] = useState(post?.categories || []);
  const [topics, setTopics] = useState(post?.topics?.join(", ") || "");
  const [ageRanges, setAgeRanges] = useState(post?.ageRanges || []);
  const [isGeneral, setIsGeneral] = useState(post?.isGeneral ?? false);
  const [isFull] = useState(post?.isFull ?? false);
  const [notify, setNotify] = useState(post?.notify ?? false);
  const [showAgeRangeModal, setShowAgeRangeModal] = useState(false);
  const [active, setActive] = useState(post?.active);

  const [media, setMedia] = useState(post?.medias || []);
  const [mainMediaUrl, setMainMediaUrl] = useState(
    post?.medias?.[post?.imageDefault || 0]?.url || ""
  );

  useEffect(() => {
    blogCategoryStore.fetchCategories();
  }, []);

  const handleCategoryToggle = (id: string) => {
    setCategories((prev) =>
      prev.includes(id) ? prev.filter((catId) => catId !== id) : [...prev, id]
    );
  };

  const handleAgeRangeToggle = (range: string) => {
    setAgeRanges((prev) =>
      prev.includes(range) ? prev.filter((r) => r !== range) : [...prev, range]
    );
  };

  const isFormValid = () => {
    const isTextComplete = title.trim() && summary.trim() && fullText.trim();
    const hasCategory = categories.length > 0;
    const hasAgeRange = isGeneral || ageRanges.length > 0;
    return isTextComplete && hasCategory && hasAgeRange;
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      Alert.alert("Atenção", "Preencha todos os campos obrigatórios e selecione categoria e faixa etária se necessário.");
      return;
    }

    const postToSave: Omit<IPost, "id"> = {
      lang,
      title,
      summary,
      fullText,
      categories,
      createdAt: post?.createdAt || Date.now(),
      updatedAt: Date.now(),
      topics: topics.split(",").map((t) => t.trim()).filter(Boolean),
      ageRanges,
      medias: media,
      imageDefault: media.findIndex((m) => m.url === mainMediaUrl),
      isGeneral,
      isFull,
      active,
      notify,
    };

    await postStore.savePost(postToSave, post?.id);
    goBack();
  };

  return (
    <ScrollView style={styles.container}>
      <Text style={styles.title}>{post ? "Editar Artigo" : "Novo Artigo"}</Text>

      <TextInput
        placeholder="Título"
        value={title}
        onChangeText={setTitle}
        style={styles.input}
      />

      <TextInput
        placeholder="Resumo"
        value={summary}
        onChangeText={setSummary}
        style={styles.input}
      />


      <LanguageSelector value={lang} onChange={setLang} />


      <Text style={styles.section}>Texto:</Text>
      <SpaceH />
      <View>
        <RichTextEditor
          initialContent={fullText}
          onChange={setFullText}
        />
      </View>

      <SpaceH size={40} />

      <Text style={styles.section}>Categorias:</Text>
      {blogCategoryStore.categories
        .filter((cat) => cat.active)
        .map((cat) => (
          <TouchableOpacity
            key={cat.id}
            onPress={() => handleCategoryToggle(cat.id)}
            style={styles.checkboxRow}
          >
            <Text>
              {categories.includes(cat.id) ? "☑️" : "⬜️"} {cat.title || "-"}
            </Text>
          </TouchableOpacity>
        ))}

      <SpaceH />

      <View style={{ flexDirection: "row" }}>
        <Text>Pra todas as idades:</Text>
        <SpaceW />
        <Switch value={isGeneral} onValueChange={setIsGeneral} />
      </View>

      {!isGeneral && (
        <>
          <Text style={styles.section}>Faixas Etárias:</Text>
          <TouchableOpacity
            onPress={() => setShowAgeRangeModal(true)}
            style={styles.input}
          >
            <Text>
              {ageRanges.length > 0
                ? `${ageRanges.length} selecionada(s)`
                : "Selecionar Faixas Etárias"}
            </Text>
          </TouchableOpacity>
        </>
      )}

      <Modal visible={showAgeRangeModal} animationType="slide">
        <ScrollView style={{ padding: 20 }}>
          <Text style={{ fontSize: 18, fontWeight: "bold", marginBottom: 10 }}>
            Selecionar Faixas Etárias
          </Text>
          {ageRangeList.map((range) => (
            <TouchableOpacity
              key={range.value}
              onPress={() => handleAgeRangeToggle(range.value)}
              style={styles.checkboxRow}
            >
              <Text>
                {ageRanges.includes(range.value) ? "☑️" : "⬜️"} {range.label}
              </Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            onPress={() => setShowAgeRangeModal(false)}
            style={styles.saveButton}
          >
            <Text style={styles.saveButtonText}>Fechar</Text>
          </TouchableOpacity>
        </ScrollView>
      </Modal>

      <Text style={styles.section}>Tópicos (separados por vírgula):</Text>
      <TextInput
        placeholder="ex: Sono, Alimentação"
        value={topics}
        onChangeText={setTopics}
        style={styles.input}
      />

      <PhotoUploader
        postId={post?.id || "temp"}
        media={media}
        setMedia={setMedia}
        mainMediaUrl={mainMediaUrl}
        setMainMediaUrl={setMainMediaUrl}
      />

      <View style={styles.switchContainer}>
        <Text>Notificar (notify):</Text>
        <Switch value={notify} onValueChange={setNotify} />
      </View>


      <View style={styles.switchContainer}>
        <Text>Ativo:</Text>
        <Switch value={active} onValueChange={setActive} />
      </View>

      <TouchableOpacity
        style={[
          styles.saveButton,
          { backgroundColor: isFormValid() ? Theme.foreground : "#ccc" },
        ]}
        onPress={handleSave}
        disabled={!isFormValid()}
      >
        <Text style={styles.saveButtonText}>Salvar</Text>
      </TouchableOpacity>
    </ScrollView>
  );
});

export default PostForm;

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, backgroundColor: "#F2F2F2" },
  title: { fontSize: 22, fontWeight: "bold", marginBottom: 20 },
  section: { marginTop: 15, fontWeight: "bold" },
  input: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    padding: 10,
    marginBottom: 10,
  },
  checkboxRow: { paddingVertical: 8 },
  switchContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  saveButton: {
    padding: 15,
    borderRadius: 5,
    marginTop: 20,
    backgroundColor: Theme.foreground,
  },
  saveButtonText: { color: "#fff", textAlign: "center", fontWeight: "bold" },
});
